import React from 'react';
import { Input } from '../../../components/ui/Input';
import { DownloadIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { ArrowLeft20Filled, ArrowLeft24Filled } from '@fluentui/react-icons';
import { Button } from '../../../components/ui/Button';

import { useBillingDetail, useDownloadBillingPdf, useExportBillingExcel } from '../../../hooks/useBilling';
import { useAuth } from '../../../hooks/useAuth';
import { useState } from 'react';

const formatCustomDate = (dateString?: string) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '-';
  const day = d.getDate();
  const month = d.toLocaleString('en-IN', { month: 'short' });
  const year = d.getFullYear();
  return `${day} ${month}, ${year}`;
};

export default function InvoiceDetailsPage() {
  const navigate = useNavigate();
  const { invoiceNumber } = useParams<{ invoiceNumber: string }>();
  const { user } = useAuth();
  const [search, setSearch] = useState('');

  const { data: details = [], isLoading } = useBillingDetail(user?.userId || user?.id, invoiceNumber);
  const { mutate: downloadPdf, isPending: isDownloadingPdf } = useDownloadBillingPdf();
  const { mutate: exportExcel, isPending: isExportingExcel } = useExportBillingExcel();

  const handleDownloadPdf = () => {
    if (invoiceNumber && user) {
      downloadPdf({ userId: user.userId || user.id, ledgerNumber: invoiceNumber });
    }
  };

  const handleExportExcel = () => {
    if (invoiceNumber && user) {
      exportExcel({ userId: user.userId || user.id, ledgerNumber: invoiceNumber });
    }
  };

  const handleGoBack = () => {
    navigate(ROUTES.INVOICE);
  };

  const filteredDetails = details.filter((d) =>
    d.trackingNumber?.toLowerCase().includes(search.toLowerCase())
  );

  const totalAmount = details.reduce((sum, item) => sum + (Number(item.freightCharges) || 0), 0);
  const ledgerDate = details.length > 0 ? formatCustomDate(details[0].createdAt) : '-';


  return (
    <div className="min-h-screen bg-background px-4 lg:px-6 py-4 lg:py-6">
      <div className="mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <div>
            <div className="flex flex-row  items-center">
              <Button
                className="bg-background text-text hover:bg-background"
                onClick={handleGoBack}
              >
                <ArrowLeft24Filled className=" cursor-pointer" />
              </Button>
              <h1 className="text-2xl lg:text-3xl font-bold text-text">
                Invoices
              </h1>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center text-sm text-text3 gap-1 lg:gap-2">
              View and download a detailed bifurcation of all charges.
            </div>
          </div>
        </div>

        {/* Ledger Card */}
        <div className="bg-[#FFF6EF] border border-[#F6D3B9] rounded-2xl px-4 lg:px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-16 gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-text text-lg lg:text-xl">
                  Ledger
                </span>
                <span className="text-sm px-2 py-0.5 rounded-full bg-white text-[#FF9126] font-medium">
                  Partial
                </span>
              </div>
              <p className="text-sm text-text mt-1 break-all">
                {invoiceNumber}
              </p>
            </div>

            <div>
              <p className="font-bold text-text text-lg lg:text-xl">Amount</p>
              <p className="text-sm text-text mt-1">₹{totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
            </div>

            <div>
              <p className="font-bold text-text text-lg lg:text-xl">Date</p>
              <p className="text-sm text-text mt-1">{ledgerDate}</p>
            </div>
          </div>
        </div>

        {/* Search + Download */}
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
          <div className="w-full lg:max-w-md">
            <Input
              placeholder="Search by Tracking Number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              className="flex items-center gap-2 rounded-lg text-white px-4 py-2 bg-teal font-medium w-fit disabled:opacity-50"
              onClick={handleDownloadPdf}
              disabled={isDownloadingPdf}
            >
              <DownloadIcon className="w-4 h-4" />
              {isDownloadingPdf ? 'Downloading PDF...' : 'Download PDF'}
            </button>
            <button
              className="flex items-center gap-2 rounded-lg text-white px-4 py-2 bg-teal font-medium w-fit disabled:opacity-50"
              onClick={handleExportExcel}
              disabled={isExportingExcel}
            >
              <DownloadIcon className="w-4 h-4" />
              {isExportingExcel ? 'Exporting...' : 'Export Excel'}
            </button>
          </div>
        </div>

        {/* Table */}
        {/* Table Section */}
        <div className="overflow-x-auto">
          <div className="space-y-3">
            {/* Detached Table Header Card */}
            <div className="bg-teal border rounded-xl px-3 xl:px-5 py-2.5 text-xs xl:text-sm font-bold text-white shadow-md">
              <div className="grid grid-cols-7 gap-2 xl:gap-4 text-center">
                <div>Delivery Date</div>
                <div>Tracking Number</div>
                <div>Courier</div>
                <div>Order ID</div>
                <div>Zone</div>
                <div>Inv. Value</div>
                <div>Shipping Charges</div>
              </div>
            </div>

            {/* Row Cards */}
            {isLoading ? (
              <div className="bg-white border rounded-xl py-20 text-center text-text3">
                Loading details...
              </div>
            ) : filteredDetails.length === 0 ? (
              <div className="bg-white border rounded-xl py-20 text-center text-text3">
                No items found for this invoice.
              </div>
            ) : (
              filteredDetails.map((row, index) => (
                <div
                  key={index}
                  className="bg-card border rounded-xl px-3 xl:px-5 py-3 shadow-sm text-center"
                >
                  <div className="grid grid-cols-7 gap-2 xl:gap-4 text-xs xl:text-sm text-gray-700 items-start">
                    {/* Delivery Date */}
                    <div className="font-medium text-text">
                      {formatCustomDate(row.pickupDate)}
                    </div>

                    {/* Tracking Number */}
                    <div>
                      <span className="text-blue-500 underline font-medium cursor-pointer">
                        {row.trackingNumber}
                      </span>
                    </div>

                    {/* Courier */}
                    <div className="text-gray-600">{row.courierId ? `Courier ${row.courierId}` : '-'}</div>

                    {/* Order ID */}
                    <div className="font-medium">{row.shipmentId || '-'}</div>

                    {/* Zone */}
                    <div className="text-gray-600">{row.zone || '-'}</div>

                    {/* Inv. Value */}
                    <div className="font-bold text-text">
                      ₹ {Number(row.invoiceValue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </div>

                    {/* Shipping Charges */}
                    <div className="font-bold text-text text-teal">
                      ₹ {Number(row.freightCharges).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Bottom Padding */}
            <div className="h-20" />
          </div>
        </div>
      </div>
    </div>
  );
}
